import type { RfxQuoteItem } from "../../types/quote.type";
import type { SupplierRow } from "../../../components/QuoteTable/quote-table.types";
import { getRowDisplayName } from "../../../components/QuoteTable/utils";
import { GST_RATE, GST_RATE_LABEL } from "../../../config/gst.config";

export interface QuoteFieldValues {
	[key: string]: Record<string, number | string>;
}

export const TABLE_HEADERS = [
	{ name: "#", width: "3%" },
	{ name: "Description of Scope", width: "40%" },
	{ name: "Unit of Measurement", width: "19%" },
	{ name: "Quantity", width: "12%" },
	{ name: "Rate", width: "12%" },
	{ name: "Cost", width: "12%" },
];

export const TOTAL_ROWS = [
	{ id: "subtotal", name: "Subtotal", color: "#475467" },
	{ id: "gst", name: GST_RATE_LABEL, color: "#475467" },
	{ id: "totalcost", name: "Total Cost", color: "#101828" },
] as const;

export type TotalType = (typeof TOTAL_ROWS)[number]["id"];

/**
 * Computes subtotal/gst/totalcost across rows.
 *
 * When `columns` is supplied the calculation mirrors the per-row total logic
 * in QuoteTableCell: quantity × rate only when there is exactly ONE
 * response_value column; otherwise each rate column is summed as a flat
 * amount and savings are added directly (already negative).
 *
 * Without `columns` it falls back to legacy behaviour (rate × quantity).
 */
export const calculateTotal = (
	type: TotalType,
	keys: string[] | "all",
	allValue: QuoteFieldValues,
	extraCost = 0,
	columns?: { id: number; columnType: string }[],
): number => {
	const keyIds = keys === "all" ? Object.keys(allValue ?? {}) : keys;

	// Derive column-type info when available
	const responseValueColIds = columns
		?.filter((c) => c.columnType === "response_value")
		.map((c) => c.id) ?? [];
	const savingColIds = columns
		?.filter((c) => c.columnType === "saving")
		.map((c) => c.id) ?? [];
	const hasQuantityCol = columns?.some((c) => c.columnType === "response_quantity") ?? false;
	const multiValueMode = columns !== undefined && responseValueColIds.length !== 1;

	const subTotal =
		keyIds.reduce((acc, key) => {
			const rowValues = allValue?.[key] ?? {};

			if (columns !== undefined) {
				// Column-aware path: mirrors QuoteTableCell total logic
				let responseRateSum = 0;
				let savingFlat = 0;
				for (const colId of responseValueColIds) {
					responseRateSum += Number(rowValues[`rate_${colId}`] ?? 0);
				}
				for (const colId of savingColIds) {
					savingFlat += Number(rowValues[`rate_${colId}`] ?? 0); // already negative
				}

				const quantity = Number(rowValues.quantity ?? 0);
				if (multiValueMode) {
					// Multiple value columns → flat sum, no quantity multiplier
					return acc + responseRateSum + savingFlat;
				}
				// Single value column → multiply by quantity (when qty col exists and qty > 0)
				const useMultiplier = hasQuantityCol && quantity > 0;
				return acc + (useMultiplier ? responseRateSum * quantity : responseRateSum) + savingFlat;
			}

			// Legacy fallback (no column info)
			const rateSum = Object.keys(rowValues).reduce((sum, field) => {
				if (field.startsWith("rate_")) {
					return sum + Number(rowValues[field] ?? 0);
				}
				return sum;
			}, 0);
			const totalRate = rateSum > 0 ? rateSum : Number(rowValues.rate ?? 0);
			const quantity = Number(rowValues.quantity ?? 0);
			return acc + totalRate * quantity;
		}, 0) + extraCost;

	switch (type) {
		case "subtotal":
			return subTotal;
		case "gst":
			return subTotal * GST_RATE;
		case "totalcost":
			return subTotal + subTotal * GST_RATE;
		default:
			return 0;
	}
};

export const handleKeyDown = (
	event: React.KeyboardEvent<HTMLInputElement>,
): void => {
	if (event.key === "-") {
		event.preventDefault();
	}
};

export const handleNumberValue = (value: string | number): number | string => {
	const numericValue = parseFloat(String(value));
	if (isNaN(numericValue) || numericValue < 0) {
		return "";
	}
	return numericValue;
};

/**
 * Maps a saved RfxQuoteItem (with its cells) plus current Formik state into a
 * SupplierRow shape used by SupplierRowsSection. Description comes from the
 * supplier-wins merge via getRowDisplayName so supplier-created rows show
 * their own description, not the procurer fallback.
 */
export const mapRowToSupplierRow = (
	row: RfxQuoteItem,
	formikValues?: QuoteFieldValues,
	columns?: { id: number; columnType: string; sortOrder?: number }[],
	localIdCounter?: React.MutableRefObject<number>,
): SupplierRow => {
	const counter = localIdCounter?.current ?? 1;
	const localId = `supplier-saved-${counter}`;
	if (localIdCounter) {
		localIdCounter.current += 1;
	}
	const key = `quote-${row.id}`;
	const cellValues = formikValues?.[key];

	const columnVals: Record<number, string> = {};
	if (cellValues && columns) {
		for (const col of columns) {
			if (col.columnType === "response_value") {
				const val = cellValues[`rate_${col.id}`];
				if (val != null) {
					columnVals[col.id] = String(val);
				}
			} else if (col.columnType === "response_quantity") {
				const val = cellValues.quantity;
				if (val != null) {
					columnVals[col.id] = String(val);
				}
			} else if (col.columnType === "response_text") {
				const val = cellValues[`text_${col.id}`];
				if (val != null) {
					columnVals[col.id] = String(val);
				}
			} else if (col.columnType === "response_number") {
				const val = cellValues[`number_${col.id}`];
				if (val != null) {
					columnVals[col.id] = String(val);
				}
			} else if (col.columnType === "saving") {
				// Saving cells share the rate_${col.id} formik key with
				// response_value; stored as a negative number. Hydrating
				// here lets the SupplierRowsSection saving input pre-fill
				// on refresh (it reads row.columnValues[col.id] and shows
				// Math.abs of the negative).
				const val = cellValues[`rate_${col.id}`];
				if (val != null) {
					columnVals[col.id] = String(val);
				}
			}
		}
	}

	return {
		localId,
		description:
			getRowDisplayName(
				row,
				columns as unknown as Parameters<typeof getRowDisplayName>[1],
			) || "",
		unitId: row.unitId ?? null,
		quantity: cellValues?.quantity != null ? String(cellValues.quantity) : "",
		rate: cellValues?.rate != null ? String(cellValues.rate) : "",
		savedId: row.id,
		columnValues: Object.keys(columnVals).length > 0 ? columnVals : undefined,
	};
};

/**
 * Returns the set of column IDs that are response_value columns.
 */
export const getValueColumnIds = (
	columns: { id: number; columnType: string }[] | undefined,
): Set<number> =>
	new Set(
		columns?.filter((c) => c.columnType === "response_value").map((c) => c.id) ??
			[],
	);

/**
 * Sums `rate_{colId}` Formik values for a row; falls back to legacy
 * `rate * quantity` when no per-column rates exist.
 */
export const sumRowRateValues = (
	rowValues: Record<string, number | string>,
	valueColIds: Set<number>,
): number => {
	let rateSum = 0;
	for (const colId of valueColIds) {
		rateSum += Number(rowValues[`rate_${colId}`]) || 0;
	}
	if (rateSum === 0) {
		const rate = Number(rowValues.rate ?? 0);
		const quantity = Number(rowValues.quantity ?? 0);
		return rate * quantity;
	}
	return rateSum;
};

/**
 * Computes per-quote { cost, perColumn } supplier-row totals from Formik
 * state. Used to seed useSupplierRowTotals on mount.
 */
export const computeInitialSupplierTotals = (
	quotes: { id: number; rows: RfxQuoteItem[]; columns?: { id: number; columnType: string }[] }[] | undefined,
	inviteId: number | undefined,
	formikValues: QuoteFieldValues | undefined,
): {
	costs: Record<number, number>;
	perColumn: Record<number, Record<number, number>>;
} => {
	const costs: Record<number, number> = {};
	const perColumn: Record<number, Record<number, number>> = {};

	quotes?.forEach((quote) => {
		const sRows = getSupplierRows(quote.rows, inviteId);
		if (sRows.length === 0) {
			return;
		}
		const valueColIds = getValueColumnIds(quote.columns);
		const cols: Record<number, number> = {};
		for (const colId of valueColIds) {
			cols[colId] = 0;
		}
		let cost = 0;
		for (const row of sRows) {
			const rowValues = formikValues?.[`quote-${row.id}`] ?? {};
			cost += sumRowRateValues(rowValues, valueColIds);
			for (const colId of valueColIds) {
				cols[colId] += Number(rowValues[`rate_${colId}`]) || 0;
			}
		}
		costs[quote.id] = cost;
		perColumn[quote.id] = cols;
	});

	return { costs, perColumn };
};

export const getClientRows = (rows: RfxQuoteItem[]): RfxQuoteItem[] =>
	rows.filter((r) => r.createdByType !== "supplier");

export const getSupplierRows = (
	rows: RfxQuoteItem[],
	inviteId?: number,
): RfxQuoteItem[] =>
	rows.filter(
		(r) =>
			r.createdByType === "supplier" &&
			(!inviteId || r.createdByInviteId === inviteId),
	);
